import React from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import AddServiceDropdown from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/components/AddServiceDropdown";
import ServiceEmptyState from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/components/ServiceEmptyState";
import DealServiceRow from "./DealServiceRow";
import FieldLabel from "./FieldLabel";
import { DealServiceLine } from "./types";
import { useDealServiceLines } from "./useDealServiceLines";

type ServiceLinesState = ReturnType<typeof useDealServiceLines>;

const MANUAL_RECURRING_REVENUE_INPUT_ID =
  "create-deal-manual-recurring-revenue";

interface Props {
  state: ServiceLinesState;
  productId: string;
  currency: string;
  // With no services to pick from, the recurring revenue is entered by hand.
  needsManualRecurringRevenue: boolean;
  manualRecurringRevenue: string;
  onManualRecurringRevenueChange: (value: string) => void;
}

const DealServicesSection: React.FC<Props> = ({
  state,
  productId,
  currency,
  needsManualRecurringRevenue,
  manualRecurringRevenue,
  onManualRecurringRevenueChange
}) => {
  const { t } = useTranslation();
  const { label: arrMrrLabel } = useArrMrr();
  const {
    lines,
    expandedKey,
    setExpandedKey,
    updateLine,
    removeLine,
    serviceOptions,
    sections,
    productName,
    hasAvailableServices
  } = state;

  const emptyDropdownContent = hasAvailableServices ? undefined : (
    <ServiceEmptyState />
  );

  const handleChange =
    (line: DealServiceLine) => (changes: Partial<DealServiceLine>) =>
      updateLine(line.key, changes);

  return (
    <div className="flex flex-col gap-2" data-test="create-deal-services">
      <Text variant="bodySecondary">
        {t("deals.createModal.services.sectionLabel")}
        <span className="text-primary ml-1">*</span>
      </Text>

      {needsManualRecurringRevenue ? (
        <div
          className="flex flex-col items-start gap-4 rounded-xl border border-dashed border-border-border1 p-4"
          data-test="create-deal-no-services"
        >
          <Text variant="bodySecondary">
            {t("deals.createModal.services.noServicesConfigured", {
              product: productName,
              term: arrMrrLabel
            })}
          </Text>
          <div className="flex w-[224px] flex-col gap-1">
            <FieldLabel
              label={t("deals.createModal.services.expectedRecurringRevenue", {
                term: arrMrrLabel
              })}
              htmlFor={MANUAL_RECURRING_REVENUE_INPUT_ID}
            />
            <Input
              id={MANUAL_RECURRING_REVENUE_INPUT_ID}
              type="number"
              min={0}
              value={manualRecurringRevenue}
              placeholder={t(
                "deals.createModal.services.expectedRecurringRevenuePlaceholder",
                { term: arrMrrLabel }
              )}
              onChange={(event) =>
                onManualRecurringRevenueChange(event.target.value)
              }
            />
          </div>
        </div>
      ) : lines.length === 0 ? (
        <div className="flex flex-col items-start gap-4 rounded-xl border border-dashed border-border-border1 p-4">
          <Text variant="bodySecondary">
            {t("deals.createModal.services.emptyStateTitle")}
          </Text>
          <AddServiceDropdown
            serviceOptions={serviceOptions}
            sections={sections}
            variant="primary"
            emptyStateContent={emptyDropdownContent}
            disabled={!productId}
            disabledTooltipText={t(
              "deals.createModal.services.addServiceDisabledTooltip"
            )}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {lines.map((line) => (
            <DealServiceRow
              key={line.key}
              line={line}
              currency={currency}
              isExpanded={expandedKey === line.key}
              onToggle={() =>
                setExpandedKey(expandedKey === line.key ? null : line.key)
              }
              onChange={handleChange(line)}
              onRemove={() => removeLine(line.key)}
            />
          ))}
          <div className="self-start">
            <AddServiceDropdown
              serviceOptions={serviceOptions}
              sections={sections}
              variant="tertiary"
              emptyStateContent={emptyDropdownContent}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DealServicesSection;
